import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { createMoneyFormatter, type MoneyPresentation } from "@facturar/money-format";
import { useProfile } from "@/lib/api/profile.queries";
import { useAuth } from "@/context/AuthContext";

export const MoneyPresentationContext = createContext<MoneyPresentation>({});

export function MoneyPresentationProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  return auth.status === "authenticated"
    ? <AuthenticatedMoneyPresentation>{children}</AuthenticatedMoneyPresentation>
    : <MoneyPresentationContext.Provider value={{}}>{children}</MoneyPresentationContext.Provider>;
}

function AuthenticatedMoneyPresentation({ children }: { children: ReactNode }) {
  const { data } = useProfile();
  const { i18n } = useTranslation();
  const locale = data?.profile?.locale ?? (i18n.language.startsWith("en") ? "en-US" : "es-CO");
  const digits = data?.profile?.monetaryDecimalPlaces ?? 2;
  const value = useMemo(() => ({ locale, monetaryDecimalPlaces: digits }), [locale, digits]);
  return <MoneyPresentationContext.Provider value={value}>{children}</MoneyPresentationContext.Provider>;
}

export function useMoneyFormatter(style: "currency" | "decimal" = "currency") {
  const preferences = useContext(MoneyPresentationContext);
  return useMemo(() => createMoneyFormatter(preferences, style), [preferences, style]);
}
